import * as React from 'react';
import { useFormikContext, useField } from 'formik';
import { AstExpression, Component } from '../../../models/be/db/FormSpecification';
import DataCaptureField from './DataCaptureField';
import { single, hasValue, safeParseInt } from '../../../lib/utils';
import { evalBoolean, evalString } from '../../../core/ast';
import Workflow from '../../../models/be/Workflow';
import { isAstExpression } from '../../../core/astInterpreter';
import BirthDetails from '../../../models/be/BirthDetails';

const UkDriverLicenceNumberField = (props: Props) => {
	const fieldName = single(props.component.fieldNames);
	const formikProps = { name: fieldName };
	const [field, meta, helpers] = useField(formikProps);
	const { values } = useFormikContext<Workflow>();
	const location = values.driverLicenceDetails?.territory;

	const isValid = (
		region: string,
		driverLicenceNumber: string,
		component: Component,
		workflow: Workflow,
		birthDetails?: BirthDetails
	) => {
		switch (region) {
			case 'Great Britain':
				return isUkDriverLicenceNumberValid(driverLicenceNumber, workflow, component, birthDetails);
			case 'Northern Ireland':
				return isNorthernIrelandDriverLicenceNumberValid(driverLicenceNumber);
			case 'Jersey':
			case 'Guernsey':
				return isJerseyGuernseyDriverLicenceNumberValid(driverLicenceNumber);
			case 'Isle of Man':
				return isIsleOfManDriverLicenceNumberValid(driverLicenceNumber);
			default:
				return isUkDriverLicenceNumberValid(driverLicenceNumber, workflow, component, birthDetails);
		}
	};
	if (/[a-z]/.test(field.value)) {
		helpers.setValue(field.value.toUpperCase());
	} else if (
		!hasValue(meta.error) &&
		(evalBoolean(props.component.props.required, values) || hasValue(field.value)) && // No need to validate a not required and empty field
		!isValid(location, field.value, props.component, values, props.visibleWorkflowFields?.birthDetails)
	) {
		const ukDriverLicenceNumberRegexErrorMessage = props.component.props.patternErrorMessage as string;
		helpers.setError(ukDriverLicenceNumberRegexErrorMessage);
	}

	// eslint-disable-next-line react/jsx-props-no-spreading
	return <DataCaptureField {...props.component} />;
};

export default UkDriverLicenceNumberField;

// Northern Ireland: 8 digits
const isNorthernIrelandDriverLicenceNumberValid = (driverLicenceNumber: string): boolean => {
	const regex = /^\d{8}$/;
	return regex.test(driverLicenceNumber);
};

// Jersey & Guernsey: 6 digits followed by an optional / and 1 or 2 digits
const isJerseyGuernseyDriverLicenceNumberValid = (driverLicenceNumber: string): boolean => {
	const regex = /^(\d{6}|\d{6}\/\d{1,2})$/;
	return regex.test(driverLicenceNumber);
};

// Isle of Man: either 2 letters, 4 digits, 1 letter OR 6 digits/1-2 digits
const isIsleOfManDriverLicenceNumberValid = (driverLicenceNumber: string): boolean => {
	const regex1 = /^[A-Z]{2}\d{4}[A-Z]$/;
	const regex2 = /^\d{6}\/\d{1,2}$/;
	return regex1.test(driverLicenceNumber) || regex2.test(driverLicenceNumber);
};

// The validation logic is based on how UK driver licence number characters are constructed:
// https://www.drivercheck.co.uk/driver-licence-information/photocard-driving-licence-explained/
const isUkDriverLicenceNumberValid = (
	driverLicenceNumber: string,
	values: Workflow,
	props: Component,
	birthDetails: BirthDetails
) => {
	const presentSurname = values.driverLicenceDetails.lastName;
	const firstMiddleNames = values.driverLicenceDetails.middleNames;
	const forename = values.driverLicenceDetails.firstName;
	const { gender } = birthDetails;
	const dateOfBirth = birthDetails.birthDate;

	if ([presentSurname, forename, gender, dateOfBirth].some((x) => !hasValue(x))) {
		return false;
	}

	const dateOfBirthArray = dateOfBirth.split('-');
	const dateOfBirthYear = dateOfBirthArray[0];
	const dateOfBirthMonth = dateOfBirthArray[1];
	const dateOfBirthDay = dateOfBirthArray[2];

	const ukDriverLicenceNumberRegexString = props.props.pattern;
	const evaluatedUkDriverLicenceNumberRegexString = isAstExpression(ukDriverLicenceNumberRegexString)
		? evalString(ukDriverLicenceNumberRegexString as AstExpression, values)
		: (ukDriverLicenceNumberRegexString as string);

	const ukDriverLicenceNumberMaxLength = props.props.maxLength;
	if (
		!checkString(driverLicenceNumber, true, ukDriverLicenceNumberMaxLength, evaluatedUkDriverLicenceNumberRegexString)
	) {
		return false;
	}

	// Check the surname
	const surname = presentSurname.replace(' ', '').toUpperCase();
	let surnameLength = surname.length;
	if (surnameLength > 5) surnameLength = 5; // UK driver licence number surname maxlength is 5.
	// GW - MAC can be shown as Mc on the DL Number so need special processing for that
	//      added 04/09/2018 - CAS-12756
	if (surname.startsWith('MAC') || surname.includes("'")) {
		if (surname.startsWith('MAC')) {
			// If the DL doesn't match
			if (driverLicenceNumber.substr(0, surnameLength) !== surname.substr(0, surnameLength)) {
				// Check if it matchs an "Mcxxxx" DL number
				const nameToCheck = `MC${surname.substr(3)}`;
				let nameToCheckLength: number = nameToCheck.length;
				if (nameToCheckLength > 5) nameToCheckLength = 5;
				if (driverLicenceNumber.substr(0, nameToCheckLength) !== nameToCheck.substr(0, nameToCheckLength)) {
					return false;
				}
			}
		}

		// Check if any apostrophes are present in surname, try match without the apostrophes
		if (surname.includes("'")) {
			const nameToCheck = surname.replace("'", '').padEnd(5, '9').substr(0, 5);
			if (nameToCheck !== driverLicenceNumber.substr(0, 5)) {
				return false;
			}
		}
	} else if (driverLicenceNumber.substr(0, surnameLength) !== surname.substr(0, surnameLength)) {
		return false;
	}

	// Check the embedded date matches
	const yearNumberInDriverLicence = Number(driverLicenceNumber.substr(5, 1) + driverLicenceNumber.substr(10, 1));
	let yearInTwoDigitsInDriverLicence = safeParseInt(dateOfBirthYear) - 1900;
	if (yearInTwoDigitsInDriverLicence >= 100) yearInTwoDigitsInDriverLicence -= 100;
	const monthNumberInDriverLicence = Number(driverLicenceNumber.substr(6, 2));
	const monthNumberFromBirthDate = gender === 'F' ? Number(dateOfBirthMonth) + 50 : Number(dateOfBirthMonth);

	const dateNumberInDriverLicence = Number(driverLicenceNumber.substr(8, 2));
	if (
		yearNumberInDriverLicence !== yearInTwoDigitsInDriverLicence ||
		monthNumberInDriverLicence !== monthNumberFromBirthDate ||
		dateNumberInDriverLicence !== Number(dateOfBirthDay)
	) {
		return false;
	}

	// check the initials match
	if (forename === null || forename === undefined) {
		return false;
	}
	if (forename[0].toUpperCase() !== driverLicenceNumber[11]) {
		return false;
	}

	if (driverLicenceNumber.length >= 13) {
		if (!firstMiddleNames && driverLicenceNumber[12] !== '9') {
			return false;
		}
		if (hasValue(firstMiddleNames) && driverLicenceNumber[12] !== firstMiddleNames[0].toUpperCase()) {
			return false;
		}
	} else return false;

	return true;
};

const checkString = (testString: string, isMandatory: boolean, maxLength: number, allowedChars: string) => {
	if (!hasValue(testString)) {
		if (isMandatory) return false;
		return true;
	}

	if ((maxLength > 0 && testString.length > maxLength) || !checkIfAllCharsAreValid(testString, allowedChars)) {
		return false;
	}

	return true;
};

const checkIfAllCharsAreValid = (inputString: string, allowedChars: string) => {
	if (!hasValue(inputString)) return true;
	const regexp = new RegExp(allowedChars);
	return regexp.test(inputString);
};

interface Props {
	component: Component;
	visibleWorkflowFields: Workflow;
}
